import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { hasScope } from '@nyce/admin-kit/api';
import { Chip, Switch, TextField } from '@nyce/admin-kit/ui';
import { SCOPES, type Environment } from '../../../api';
import { canOpen } from '../../../nav';
import { api, useHubLogin } from '../../../session';
import { identityPath, useTargets } from '../../../identity/session';
import { logsLink } from '../../../lib/logFilters';
import { setupFor } from '../../api/setup';
import { buddyPath } from '../../shared';
import './setup.css';

/** The active environment and its setup routes (the shell remounts the page when the environment changes). */
export function useSetup() {
  const { active } = useTargets();
  return useMemo(() => ({ target: active, api: setupFor(active) }), [active]);
}

/** HTTP status of a failed call (buddy3 errors arrive as `AuthApiError`). */
export const statusOf = (e: unknown): number | undefined => (e as { status?: number } | null)?.status;
export const messageOf = (e: unknown): string => (e as Error)?.message || String(e);

/** One URL query key as state (`replace`, so the editor and tabs do not flood the history). */
export function useQueryParam(key: string): [string, (v: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const set = (v: string | null) =>
    setParams(
      (cur) => {
        const next = new URLSearchParams(cur);
        if (v == null || v === '') next.delete(key);
        else next.set(key, v);
        return next;
      },
      { replace: true }
    );
  return [params.get(key) ?? '', set];
}

/** Several keys at once (opening an editor also resets its tab). */
export function useQueryPatch() {
  const [, setParams] = useSearchParams();
  return (patch: Record<string, string | null>) =>
    setParams(
      (cur) => {
        const next = new URLSearchParams(cur);
        for (const [k, v] of Object.entries(patch))
          if (v == null || v === '') next.delete(k);
          else next.set(k, v);
        return next;
      },
      { replace: true }
    );
}

export function EnabledChip({ enabled }: { enabled: boolean }) {
  return (
    <Chip size="sm" className={enabled ? 'nyk-tone-green' : 'nyk-tone-grey'}>
      {enabled ? 'enabled' : 'disabled'}
    </Chip>
  );
}

/** A labelled switch that can be read-only (the registry's `SwitchField` cannot). */
export function SwitchRow({
  label,
  checked,
  onChange,
  disabled,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  hint?: React.ReactNode;
}) {
  return (
    <div className="nyk-ui-field nyk-switch-field">
      <Switch checked={checked} onChange={onChange} label={label} disabled={disabled} />
      {hint && <span className="nyk-ui-hint">{hint}</span>}
    </div>
  );
}

/**
 * A write-only secret: never shown, the hint says whether one is stored; an empty field keeps it (the caller sends the
 * value only when typed).
 */
export function SecretField({
  label,
  value,
  stored,
  isNew,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  stored?: boolean;
  isNew: boolean;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <TextField
      label={label}
      type="password"
      autoComplete="new-password"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      placeholder={stored ? '•••••••• stored' : 'not set'}
      hint={
        <span data-secret={label}>
          {stored ? 'Stored — leave empty to keep it' : isNew ? 'Stored encrypted' : 'Not set'}
        </span>
      }
    />
  );
}

/** The Usage page drilled to one organization or system, same environment. */
export const usageDrillPath = (kind: 'organization' | 'system' | 'tenant', id: string, target: string) =>
  buddyPath(`/buddy/usage?groupBy=${kind}&drill=${encodeURIComponent(`${kind}:${id}`)}`, target);

export function UsageLink({
  kind,
  id,
  target,
}: {
  kind: 'organization' | 'system' | 'tenant';
  id: string;
  target: string;
}) {
  return (
    <Link className="nyk-link" to={usageDrillPath(kind, id, target)} data-link="usage">
      Usage
    </Link>
  );
}

let envCache: { at: number; data: Promise<Environment[]> } | null = null;

/** The hub's environments (any hub scope may read them), cached for 2 minutes; empty when unreadable. */
function useHubEnvironments(): Environment[] {
  const [list, setList] = useState<Environment[]>([]);
  useEffect(() => {
    let alive = true;
    if (!envCache || Date.now() - envCache.at > 120_000)
      envCache = { at: Date.now(), data: api.environments().catch(() => []) };
    void envCache.data.then((l) => alive && setList(l));
    return () => {
      alive = false;
    };
  }, []);
  return list;
}

/** Test hook. */
export const resetSetupLinks = () => {
  envCache = null;
};

/**
 * H7: from a nyce.buddy tenant/system to the rest of the hub — the AUTH tenant in Identity (same environment), and the
 * registered environments whose `authTenantId` is that tenant (its pods, logs and database). AUTH tenant ids are
 * GUIDs, so a match can only be the same installation. Every link only when the operator may open it.
 */
export function AuthTenantLinks({ tenantId, target }: { tenantId?: string | null; target: string }) {
  const scopes = useHubLogin()?.scopes;
  const envs = useHubEnvironments();
  if (!tenantId) return null;
  const id = tenantId.toLowerCase();
  const matches = envs.filter((e) => e.authTenantId?.toLowerCase() === id);
  const identity = canOpen(scopes, '/identity/tenants');
  if (!identity && !matches.length) return null;
  return (
    <div className="bd-links" data-testid="tenant-links" data-tenant-links={tenantId}>
      {identity && (
        <Link
          className="nyk-link"
          to={identityPath(`/identity/tenants/${encodeURIComponent(tenantId)}`, target)}
          data-link="identity"
        >
          AUTH tenant
        </Link>
      )}
      {matches.map((e) => (
        <span key={e.id} className="bd-links" data-env={e.id}>
          <Chip size="sm" title={`Hub environment ${e.id}`}>
            {e.name}
          </Chip>
          {e.clusterId && hasScope(scopes, SCOPES.podsRead) && (
            <Link className="nyk-link" to={`/pods?env=${encodeURIComponent(e.id)}`}>
              Pods
            </Link>
          )}
          {e.clusterId && hasScope(scopes, SCOPES.gcplogsRead) && (
            <Link className="nyk-link" to={logsLink(e.id)}>
              Logs
            </Link>
          )}
          {e.dbServerId && hasScope(scopes, SCOPES.dbRead) && (
            <Link
              className="nyk-link"
              to={`/databases?server=${encodeURIComponent(e.dbServerId)}&db=${encodeURIComponent(e.dbName)}`}
            >
              Database
            </Link>
          )}
        </span>
      ))}
    </div>
  );
}
